import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { formatRate, REPORT_LABELS, REPORT_TYPES, STOP_REASON_LABELS, type ReportQuery, type ReportType } from '@pulsepoint/shared';
import { ArrowLeft, Download, Flag, Printer } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useOrganisations } from '../../api/organisations';
import { reportExportUrl, useReport, type ChargerReportRow, type DriverReportRow, type Report, type SessionReportRow, type RemittanceReportRow, type RemittanceChargerRow, type RemittanceSessionRow, remittanceCents, sessionRemittanceCents } from '../../api/reports';
import { localTimezone } from '../../api/sessions';
import { usePermission } from '../../auth/permissions';
import { ChoiceChip } from '../../components/ChoiceChip';
import { DataTable, type Column } from '../../components/DataTable';
import { useDialog } from '../../components/dialogs/DialogProvider';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { Stat, StatStrip } from '../../components/StatStrip';
import { placeOf } from '../../lib/place';
import { colours } from '../../theme';
import { kwhOf, whereCharged, whoCharged } from '../sessions/format';
import { SessionDialog } from '../sessions/SessionDialog';
import { PowersensorReport } from '../powersensor/PowersensorReport'; // Powersensor is being retired

/** The Powersensor tab isn't one of the shared report types, so it goes when Powersensor does. */
const POWERSENSOR = 'powersensor';

const PAGE_SIZE = 50;
const ymd = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
/** The last three months as chips (this one so far), then "Choose dates" for anything else. */
type Range = 0 | 1 | 2 | 'custom';
const MONTH_CHIPS = [2, 1, 0] as const;
const monthName = new Intl.DateTimeFormat('en-AU', { month: 'long' });

function monthChip(back: number, now = new Date()) {
  const first = new Date(now.getFullYear(), now.getMonth() - back, 1);
  return back === 0 ? `${monthName.format(first)} (so far)` : monthName.format(first);
}

/** [from, to] as whole days, both inclusive, in the browser's time zone. */
function rangeDays(range: Range, custom: { from: string; to: string }, now = new Date()): { from: string; to: string } {
  const y = now.getFullYear();
  const m = now.getMonth();
  if (range === 'custom') return { from: custom.from || ymd(new Date(y, m, 1)), to: custom.to || ymd(now) };
  return { from: ymd(new Date(y, m - range, 1)), to: range === 0 ? ymd(now) : ymd(new Date(y, m - range + 1, 0)) };
}

const day = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
const dayTime = new Intl.DateTimeFormat('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false });
const monthYear = new Intl.DateTimeFormat('en-AU', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const fromYmd = (s: string) => new Date(`${s}T00:00:00`);

/** "13h 35m", "49m". */
function hoursMinutes(sec: number): string {
  const m = Math.round(sec / 60);
  return m < 60 ? `${m}m` : `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, '0')}m`;
}
const kg = (v: number | null) => (v == null ? '—' : v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const cell = (main: string, sub?: string | null) => (
  <Box>
    <Typography sx={{ fontWeight: 500 }}>{main}</Typography>
    {sub && <Typography variant="body2" color="textSecondary">{sub}</Typography>}
  </Box>
);
const num = (v: string, bold = false) => <Typography className="num" sx={{ fontWeight: bold ? 500 : 400, whiteSpace: 'nowrap' }}>{v}</Typography>;

/** Reports: a few ready-made reports over a date range, with totals and Excel, CSV and PDF. */
export function ReportsPage() {
  const [params, setParams] = useSearchParams();
  const type = (REPORT_TYPES as readonly string[]).includes(params.get('type') ?? '') ? (params.get('type') as ReportType) : 'sessions';
  const isPowersensor = params.get('type') === POWERSENSOR;
  const orgs = useOrganisations();
  const canExport = usePermission('reports.export');
  // Powersensor only shows when an organisation has it turned on.
  const powersensorOn = (orgs.data ?? []).some((o) => o.powersensorEnabled);
  // The Powersensor report only lists organisations with it turned on.
  const orgChoices = (orgs.data ?? []).filter((o) => !isPowersensor || o.powersensorEnabled);
  const [range, setRange] = useState<Range>(0);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const [organisationId, setOrganisationId] = useState('');
  const [hideZero, setHideZero] = useState(true);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [type, range, custom, organisationId, hideZero]);
  // Switching to Powersensor with an organisation chosen that doesn't have it: all of them instead.
  useEffect(() => {
    if (organisationId && orgs.data && !orgChoices.some((o) => o.id === organisationId)) setOrganisationId('');
  }, [organisationId, orgs.data, orgChoices]);

  const days = rangeDays(range, custom);
  const badDates = days.from > days.to;
  const query: ReportQuery = useMemo(
    () => ({ ...days, tz: localTimezone(), organisationId: organisationId || undefined, hideZero: hideZero ? 'true' : 'false', page, pageSize: PAGE_SIZE }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [days.from, days.to, organisationId, hideZero, page],
  );
  const report = useReport(type, query, !isPowersensor);
  const data = report.data && report.data.type === type ? report.data : undefined;
  const t = data?.totals;
  const orgName = organisationId ? orgs.data?.find((o) => o.id === organisationId)?.businessName : undefined;
  const span = `${day.format(fromYmd(days.from))} to ${day.format(fromYmd(days.to))}`;

  return (
    <>
      <PageHeader
        title="Reports."
        lead={isPowersensor ? <>Powersensor home charging, {span}{orgName ? ` · ${orgName}` : ''}.</> : <>{REPORT_LABELS[type]}, {span}{orgName ? ` · ${orgName}` : ''}. {type === 'remittance' ? "Each site's sessions are counted in its own time zone." : <>Times in {localTimezone().replace('_', ' ')} time.</>}</>}
        actions={
          canExport && !isPowersensor && (
            <Box className="no-print" sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Button component="a" href={reportExportUrl(type, query, 'xlsx')} startIcon={<Download size={20} strokeWidth={1.75} />} disabled={!data?.total}>Excel</Button>
              <Button component="a" href={reportExportUrl(type, query, 'csv')} disabled={!data?.total}>CSV</Button>
              <Button startIcon={<Printer size={20} strokeWidth={1.75} />} onClick={() => window.print()} disabled={!data?.total}>Print or PDF</Button>
            </Box>
          )
        }
      />

      <Box className="no-print">
        <Tabs value={isPowersensor ? POWERSENSOR : type} onChange={(_, v: string) => setParams({ type: v }, { replace: true })} variant="scrollable" allowScrollButtonsMobile sx={{ mb: 3, borderBottom: `1px solid ${colours.hairline}` }}>
          {REPORT_TYPES.map((r) => <Tab key={r} value={r} label={REPORT_LABELS[r]} />)}
          {(powersensorOn || isPowersensor) && <Tab value={POWERSENSOR} label="Powersensor" />}
        </Tabs>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3, alignItems: { md: 'center' }, flexWrap: 'wrap', rowGap: 1.5 }}>
          <Box role="group" aria-label="Dates" sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {MONTH_CHIPS.map((back) => (
              <ChoiceChip key={back} label={monthChip(back)} selected={range === back} onClick={() => setRange(back)} />
            ))}
            <ChoiceChip label="Choose dates" selected={range === 'custom'} onClick={() => setRange('custom')} />
          </Box>
          {range === 'custom' && (
            <Stack direction="row" spacing={1}>
              <TextField size="small" type="date" label="From" value={custom.from || days.from} onChange={(e) => setCustom({ ...custom, from: e.target.value, to: custom.to || days.to })} slotProps={{ inputLabel: { shrink: true } }} />
              <TextField size="small" type="date" label="To" value={custom.to || days.to} onChange={(e) => setCustom({ ...custom, to: e.target.value, from: custom.from || days.from })} error={badDates} helperText={badDates ? 'Before the start' : undefined} slotProps={{ inputLabel: { shrink: true } }} />
            </Stack>
          )}
          {orgChoices.length > 1 && (
            <TextField select size="small" value={organisationId} onChange={(e) => setOrganisationId(e.target.value)} sx={{ width: { xs: '100%', md: 260 } }} slotProps={{ htmlInput: { 'aria-label': 'Organisation' }, select: { displayEmpty: true } }}>
              <MenuItem value="">All organisations</MenuItem>
              {orgChoices.map((o) => <MenuItem key={o.id} value={o.id}>{o.businessName}</MenuItem>)}
            </TextField>
          )}
          {!isPowersensor && type !== 'remittance' && <FormControlLabel control={<Switch checked={hideZero} onChange={(e) => setHideZero(e.target.checked)} />} label="Hide 0 kWh sessions" sx={{ ml: { md: 1 } }} />}
        </Stack>
      </Box>

      {isPowersensor ? (
        <PowersensorReport from={days.from} to={days.to} tz={localTimezone()} organisationId={organisationId} canExport={canExport} />
      ) : (
        <>
          {type === 'remittance' ? (
            <RemittanceStats rows={(data as Report<'remittance'> | undefined)?.rows} />
          ) : (
          <StatStrip columns={4}>
            <Stat label="Sessions" value={t ? t.sessions : null} sub={t ? (t.zeroSessions ? `+ ${t.zeroSessions.toLocaleString('en-AU')} at 0 kWh${hideZero ? ', hidden' : ''}` : 'None at 0 kWh') : ' '} />
            <Stat label="Energy" value={t ? Number((t.energyWh / 1000).toFixed(1)) : null} unit="kWh" sub={t ? `${hoursMinutes(t.durationSec)} plugged in` : ' '} />
            <Stat label="CO₂ avoided, estimate" value={t ? t.co2Kg : null} unit="kg" sub="Against a petrol car" />
            <Stat
              tone={t?.duplicates ? 'fault' : undefined}
              label={t?.duplicates ? 'Need a look' : 'Duplicates'}
              value={t ? t.duplicates : null}
              sub={t ? (t.duplicates ? <Typography variant="body2" component="span" sx={{ color: colours.faultRed }}>{t.duplicates === 1 ? 'possible duplicate' : 'possible duplicates'}</Typography> : 'None found') : ' '}
            />
          </StatStrip>
          )}

          {badDates ? (
            <EmptyState title="Check the dates">The start date is after the end date.</EmptyState>
          ) : (
            <ReportTable type={type} data={data} loading={report.isPending} error={report.error} onRetry={() => void report.refetch()} page={page} onPage={setPage} organisationId={organisationId} onOrganisation={setOrganisationId} />
          )}
          {t && hideZero && type !== 'remittance' && t.zeroSessions > 0 && (
            <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }} className="no-print">
              {t.zeroSessions.toLocaleString('en-AU')} {t.zeroSessions === 1 ? 'session' : 'sessions'} with 0 kWh {t.zeroSessions === 1 ? 'is' : 'are'} hidden: plugged in, no energy delivered.{' '}
              <Button size="small" variant="text" onClick={() => setHideZero(false)} sx={{ p: 0, minWidth: 0, textDecoration: 'underline', verticalAlign: 'baseline' }}>Show them</Button>
            </Typography>
          )}
        </>
      )}
    </>
  );
}


function ReportTable({ type, data, loading, error, onRetry, page, onPage, organisationId, onOrganisation }: { type: ReportType; data: Report<ReportType> | undefined; loading: boolean; error: Error | null; onRetry(): void; page: number; onPage(p: number): void; organisationId: string; onOrganisation(id: string): void }) {
  const dialog = useDialog();
  const empty = <EmptyState title="Nothing in this range">Try a longer date range, or another organisation.</EmptyState>;
  const common = { loading, error, onRetry, empty, label: REPORT_LABELS[type] };

  if (type === 'sessions') {
    const columns: Column<SessionReportRow>[] = [
      { key: 'driver', header: 'Driver and card', render: (s) => { const w = whoCharged(s); return cell(w.title, w.sub); } },
      { key: 'charger', header: 'Charger and place', hideBelow: 'md', render: (s) => cell(s.chargerName, whereCharged(s)) },
      { key: 'start', header: 'Start', render: (s) => num(dayTime.format(new Date(s.startedAt))) },
      { key: 'plugged', header: 'Plugged in', align: 'right', hideBelow: 'sm', render: (s) => num(hoursMinutes(s.durationSec)) },
      { key: 'kwh', header: 'kWh', align: 'right', render: (s) => num(kwhOf(s.energyWh, 3), true) },
      { key: 'co2', header: 'CO₂, kg', align: 'right', hideBelow: 'md', render: (s) => num(kg(s.co2Kg)) },
      {
        key: 'flag',
        header: 'Stop reason or flag',
        hideBelow: 'md',
        render: (s) =>
          s.possibleDuplicate ? (
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, color: colours.faultRed, fontWeight: 500, whiteSpace: 'nowrap' }}>
              <Flag size={16} strokeWidth={1.75} aria-hidden /> Possible duplicate
            </Box>
          ) : (
            <Typography color="textSecondary">{s.status === 'active' ? 'Charging' : s.stopReason ? (STOP_REASON_LABELS[s.stopReason] ?? s.stopReason) : '—'}</Typography>
          ),
      },
    ];
    const d = data as Report<'sessions'> | undefined;
    return (
      <DataTable
        {...common}
        rows={d?.rows}
        columns={columns}
        rowKey={(s) => s.id}
        onRowClick={(s) => void dialog.open(SessionDialog, { sessionId: s.id })}
        server={{ total: d?.total ?? 0, page, pageSize: PAGE_SIZE, onPage, sort: { key: 'start', direction: 'desc' }, onSort: () => undefined }}
      />
    );
  }

  if (type === 'drivers' || type === 'daily') {
    // Drivers at the private price (or their card's own rate), guests at the public price; both include Infinity EV's rate.
    const money = (cents: number) => num(cents ? formatRate(cents / 100) : '—');
    const columns: Column<DriverReportRow>[] = [
      {
        key: 'period',
        header: type === 'daily' ? 'Day' : 'Month',
        sortValue: (r) => r.period,
        render: (r) => num(type === 'daily' ? day.format(fromYmd(r.period)) : monthYear.format(new Date(`${r.period}-01T00:00:00Z`))),
      },
      { key: 'driver', header: 'Driver and cards', sortValue: (r) => `${r.guests ? 1 : 0}${r.driverName.toLowerCase()}`, render: (r) => cell(r.driverName, r.guests ? 'Paid by card with the QR code, at the public price' : [r.cards, r.organisationName].filter(Boolean).join(' · ')) },
      { key: 'sessions', header: 'Sessions', align: 'right', sortValue: (r) => r.sessions, render: (r) => num(String(r.sessions)) },
      { key: 'plugged', header: 'Plugged in', align: 'right', hideBelow: 'md', sortValue: (r) => r.durationSec, render: (r) => num(hoursMinutes(r.durationSec)) },
      { key: 'kwh', header: 'kWh', align: 'right', sortValue: (r) => r.energyWh, render: (r) => num(kwhOf(r.energyWh, 1)) },
      { key: 'energyCost', header: 'Energy', align: 'right', hideBelow: 'sm', sortValue: (r) => r.energyCostCents, render: (r) => money(r.energyCostCents) },
      { key: 'idle', header: 'Idle fees', align: 'right', hideBelow: 'sm', sortValue: (r) => r.idleFeeCents, render: (r) => money(r.idleFeeCents) },
      { key: 'total', header: 'Cost', align: 'right', sortValue: (r) => r.energyCostCents + r.idleFeeCents, render: (r) => <Typography className="num" sx={{ fontWeight: 500, whiteSpace: 'nowrap' }}>{formatRate((r.energyCostCents + r.idleFeeCents) / 100)}</Typography> },
    ];
    const rows = (data as Report<'drivers'> | undefined)?.rows;
    const total = (rows ?? []).reduce((sum, r) => sum + r.energyCostCents + r.idleFeeCents, 0);
    return (
      <>
        <DataTable {...common} rows={rows} columns={columns} rowKey={(r) => `${r.period}-${r.guests ? 'guests' : (r.driverId ?? r.driverName)}-${r.organisationName}`} pageSize={PAGE_SIZE} />
        {rows && rows.length > 0 && (
          <Typography color="textSecondary" sx={{ mt: 2 }}>
            Total <strong className="num" style={{ fontWeight: 600, color: colours.nearBlack }}>{formatRate(total / 100)}</strong>. Drivers are costed at each charger’s private price (or their card’s own rate), guests at the public price they paid. Both include Infinity EV’s rate.
          </Typography>
        )}
      </>
    );
  }

  if (type === 'chargers') {
    const columns: Column<ChargerReportRow>[] = [
      { key: 'charger', header: 'Charger and place', sortValue: (r) => r.chargerName.toLowerCase(), render: (r) => cell(r.chargerName, [placeOf(r), r.bay ? `Bay ${r.bay}` : null].filter(Boolean).join(' · ')) },
      { key: 'drivers', header: 'Drivers', align: 'right', hideBelow: 'sm', sortValue: (r) => r.drivers, render: (r) => num(String(r.drivers)) },
      { key: 'sessions', header: 'Sessions', align: 'right', sortValue: (r) => r.sessions, render: (r) => num(String(r.sessions)) },
      { key: 'plugged', header: 'Plugged in', align: 'right', hideBelow: 'md', sortValue: (r) => r.durationSec, render: (r) => num(hoursMinutes(r.durationSec)) },
      { key: 'kwh', header: 'kWh', align: 'right', sortValue: (r) => r.energyWh, render: (r) => num(kwhOf(r.energyWh, 1), true) },
      { key: 'co2', header: 'CO₂, kg', align: 'right', hideBelow: 'md', sortValue: (r) => r.co2Kg, render: (r) => num(kg(r.co2Kg)) },
      { key: 'paid', header: 'Paid by guests', align: 'right', hideBelow: 'sm', sortValue: (r) => r.paidCents, render: (r) => num(r.paidCents ? formatRate(r.paidCents / 100) : '—') },
    ];
    return <DataTable {...common} rows={(data as Report<'chargers'> | undefined)?.rows} columns={columns} rowKey={(r) => r.chargerId} initialSort={{ key: 'kwh', direction: 'desc' }} pageSize={PAGE_SIZE} />;
  }

  // Remittance: one row per organisation; money in cents.
  const money = (cents: number, bold = false) => num(cents ? formatRate(cents / 100) : '—', bold);
  const sessionsSub = (r: RemittanceReportRow) => [`${r.publicSessions} guest, ${r.privateSessions} driver sessions`, r.timezones !== 'Australia/Sydney' ? `Months in ${r.timezones.replace(/_/g, ' ')} time` : null].filter(Boolean).join(' · ');
  const drill = !!organisationId;
  const columns: Column<RemittanceReportRow>[] = [
    {
      key: 'org',
      header: 'Organisation',
      sortValue: (r) => r.organisationName.toLowerCase(),
      render: (r) => (
        <Box>
          <Typography sx={{ fontWeight: 500, textDecoration: drill ? undefined : 'underline', textDecorationColor: colours.hairline, textUnderlineOffset: 3 }}>{r.organisationName}</Typography>
          <Typography variant="body2" color="textSecondary">{sessionsSub(r)}</Typography>
        </Box>
      ),
    },
    { key: 'kwh', header: 'kWh', align: 'right', hideBelow: 'md', sortValue: (r) => r.energyWh, render: (r) => num(kwhOf(r.energyWh, 1)) },
    { key: 'paid', header: 'Paid by\nguests', align: 'right', wrapHeader: true, sortValue: (r) => r.publicPaidCents, render: (r) => money(r.publicPaidCents) },
    { key: 'refunds', header: 'Refunds', align: 'right', wrapHeader: true, hideBelow: 'md', sortValue: (r) => r.refundCents, render: (r) => money(r.refundCents) },
    { key: 'card', header: 'Card\nfees', align: 'right', wrapHeader: true, hideBelow: 'sm', sortValue: (r) => r.cardFeeCents, render: (r) => money(r.cardFeeCents) },
    { key: 'ievPublic', header: 'Infinity EV,\nguests', align: 'right', wrapHeader: true, hideBelow: 'sm', sortValue: (r) => r.infinityevPublicCents, render: (r) => money(r.infinityevPublicCents) },
    {
      key: 'ievPrivate',
      header: 'Infinity EV,\ndrivers',
      align: 'right',
      wrapHeader: true,
      hideBelow: 'sm',
      sortValue: (r) => r.infinityevPrivateCents,
      render: (r) => (
        <Box>
          {money(r.infinityevPrivateCents)}
          {r.privateChargedCents > 0 && <Typography variant="body2" color="textSecondary" className="num" sx={{ whiteSpace: 'nowrap' }}>of {formatRate(r.privateChargedCents / 100)} charged</Typography>}
        </Box>
      ),
    },
    {
      key: 'remittance',
      header: 'Remittance',
      align: 'right',
      sortValue: (r) => remittanceCents(r),
      render: (r) => {
        const c = remittanceCents(r);
        return (
          <Box>
            <Typography className="num" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: c < 0 ? colours.faultRed : undefined }}>{formatRate(c / 100)}</Typography>
            {c < 0 && <Typography variant="body2" sx={{ color: colours.faultRed, whiteSpace: 'nowrap' }}>Owed to Infinity EV</Typography>}
            {r.noShare > 0 && (
              <Box component="span" title="Their price was set before Infinity EV's rate was kept, so no share is counted for them." sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: colours.faultRed, fontSize: 14, whiteSpace: 'nowrap' }}>
                <Flag size={14} strokeWidth={1.75} aria-hidden /> {r.noShare} without a rate
              </Box>
            )}
          </Box>
        );
      },
    },
  ];
  const remittance = data as Report<'remittance'> | undefined;
  const rows = remittance?.rows;
  const chargerColumns = [
    { key: 'charger', header: 'Charger and site', sortValue: (r: RemittanceChargerRow) => r.chargerName.toLowerCase(), render: (r: RemittanceChargerRow) => cell(r.chargerName, [r.organisationSiteCount > 1 ? r.siteName : null, sessionsSub(r)].filter(Boolean).join(' · ')) },
    ...columns.slice(1),
  ] as Column<RemittanceChargerRow>[];
  const siteTime = (s: RemittanceSessionRow) => new Intl.DateTimeFormat('en-AU', { timeZone: s.timezone, day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(s.startedAt));
  const sessionColumns: Column<RemittanceSessionRow>[] = [
    { key: 'start', header: 'Start,\nsite time', wrapHeader: true, sortValue: (s) => s.startedAt, render: (s) => num(siteTime(s)) },
    { key: 'driver', header: 'Driver or guest', render: (s) => { const w = whoCharged(s); return cell(w.title, w.sub); } },
    { key: 'charger', header: 'Charger', hideBelow: 'md', sortValue: (s) => s.chargerName.toLowerCase(), render: (s) => cell(s.chargerName, s.organisationSiteCount > 1 ? s.siteName : null) },
    { key: 'kwh', header: 'kWh', align: 'right', sortValue: (s) => s.energyWh, render: (s) => num(kwhOf(s.energyWh, 3)) },
    {
      key: 'price',
      header: 'Price\nper kWh',
      align: 'right',
      wrapHeader: true,
      hideBelow: 'md',
      render: (s) => (
        <Box>
          {num(s.pricePerKwh == null ? '—' : formatRate(s.pricePerKwh))}
          {s.infinityevRatePerKwh != null && <Typography variant="body2" color="textSecondary" className="num" sx={{ whiteSpace: 'nowrap' }}>Infinity EV {formatRate(s.infinityevRatePerKwh)}</Typography>}
        </Box>
      ),
    },
    { key: 'charged', header: 'Paid or\ncharged', align: 'right', wrapHeader: true, sortValue: (s) => s.chargedCents, render: (s) => money(s.chargedCents) },
    { key: 'fees', header: 'Refund and\ncard fee', align: 'right', wrapHeader: true, hideBelow: 'sm', sortValue: (s) => s.refundCents + s.cardFeeCents, render: (s) => money(s.refundCents + s.cardFeeCents) },
    { key: 'iev', header: 'Infinity EV', align: 'right', wrapHeader: true, hideBelow: 'sm', sortValue: (s) => s.infinityevCents, render: (s) => money(s.infinityevCents) },
    {
      key: 'remittance',
      header: 'Remittance',
      align: 'right',
      sortValue: (s) => sessionRemittanceCents(s),
      render: (s) => {
        const c = sessionRemittanceCents(s);
        return (
          <Box>
            <Typography className="num" sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: c < 0 ? colours.faultRed : undefined }}>{formatRate(c / 100)}</Typography>
            {s.noShare && (
              <Box component="span" title="Its price has no Infinity EV rate, so no share is counted." sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: colours.faultRed, fontSize: 14, whiteSpace: 'nowrap' }}>
                <Flag size={14} strokeWidth={1.75} aria-hidden /> No rate
              </Box>
            )}
          </Box>
        );
      },
    },
  ];
  const heading = (text: string) => <Typography variant="h6" sx={{ mt: 4, mb: 1.5 }}>{text}</Typography>;
  return (
    <>
      {drill && (
        <Button className="no-print" variant="text" startIcon={<ArrowLeft size={18} strokeWidth={1.75} />} onClick={() => onOrganisation('')} sx={{ mb: 1.5, px: 0 }}>
          All organisations
        </Button>
      )}
      <DataTable {...common} rows={rows} columns={columns} rowKey={(r) => r.organisationId} initialSort={{ key: 'org', direction: 'asc' }} pageSize={PAGE_SIZE} onRowClick={drill ? undefined : (r) => onOrganisation(r.organisationId)} />
      {drill && remittance?.chargers && remittance.chargers.length > 0 && (
        <>
          {heading('By charger')}
          <DataTable {...common} label="Remittance by charger" rows={remittance.chargers} columns={chargerColumns} rowKey={(r) => r.chargerId} initialSort={{ key: 'charger', direction: 'asc' }} pageSize={PAGE_SIZE} />
          {heading('Sessions')}
          <DataTable {...common} label="Remittance by session" rows={remittance.sessions} columns={sessionColumns} rowKey={(s) => s.id} initialSort={{ key: 'start', direction: 'asc' }} pageSize={PAGE_SIZE} onRowClick={(s) => void dialog.open(SessionDialog, { sessionId: s.id })} />
        </>
      )}
      {rows && rows.length > 0 && (
        <Typography color="textSecondary" sx={{ mt: 2 }}>
          Remittance is what guests paid, less refunds, card fees and Infinity EV’s share, less Infinity EV’s share of drivers’ RFID charging (charged even when a card has its own rate, including $0). Each session uses the price it was charged at, and counts in the month it was in at its site. {drill ? 'The Excel file has sheets by charger and by session.' : 'Click an organisation for its chargers and sessions. The Excel file has a second sheet by charger.'}
        </Typography>
      )}
    </>
  );
}

/** Remittance totals for everything shown. */
function RemittanceStats({ rows }: { rows: RemittanceReportRow[] | undefined }) {
  const sum = (f: (r: RemittanceReportRow) => number) => (rows ? rows.reduce((t, r) => t + f(r), 0) : null);
  const dollars = (c: number | null) => (c == null ? null : `${c < 0 ? '−' : ''}$${Math.abs(c / 100).toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
  const paid = sum((r) => r.publicPaidCents);
  const iev = sum((r) => r.infinityevPublicCents + r.infinityevPrivateCents);
  const fees = sum((r) => r.cardFeeCents + r.refundCents);
  const owed = sum(remittanceCents);
  return (
    <StatStrip columns={4}>
      <Stat label="Paid by guests" value={dollars(paid)} sub={rows ? `${sum((r) => r.publicSessions)} guest sessions` : ' '} />
      <Stat label="Infinity EV" value={dollars(iev)} sub="Its rate on guests' and drivers' charging" />
      <Stat label="Card fees and refunds" value={dollars(fees)} sub="Guests' payments only" />
      <Stat label="Remittance" value={dollars(owed)} sub="Owed to organisations" />
    </StatStrip>
  );
}
